import { Component, EventEmitter, Input, Output, TemplateRef } from '@angular/core';
import { StoreLoader, Unsubscriber } from '@core-lib/classes';
import { IAction, IColumnSettings, IDatagridSettings, IDatagridSort, IList } from '@core-lib/models';
import { DataTableConfigService } from '@core-lib/services/data-table-config.service';
import { Subject, of } from 'rxjs';

@Component({
    selector: 'data-table-v2',
    templateUrl: './data-table-v2.component.html',
    styles: [],
    standalone: false
})
export class DataTableComponentV2<Item, SortableCols> extends Unsubscriber {
	@Input() settings: IDatagridSettings<SortableCols>;
	@Input() withPagination = true;
	@Input() translationPath: string = '';
	@Input() actionPermission: string = '';
	@Input() columnSettings: Array<IColumnSettings> = [];

	@Input() withActions: boolean = true;
	@Input() buttonActions: TemplateRef<any>;
	@Input() SpecialColumnTreatement: TemplateRef<any>;

	@Input() list: IList<Item>;
	@Input() withCheckBox: boolean = false;
	@Input() specialCheckbox: boolean = false;
	@Input() SpecialCheckboxTreatement: TemplateRef<any>;
	@Input() selectAll: boolean = false;
	@Input() rowIdPrefix: string = 'item';

	selectedRow: number = null;
	loading: boolean = false;
	nbCheckedElements: number = 0;

	_isAllRowsCheked: boolean;
	@Input() set isAllRowsCheked(value: boolean) {
		if (value != null) {
			this._isAllRowsCheked = value;
			if (value == false) {
				this.nbCheckedElements = 0;
			}
		}
	}
	get isAllRowsCheked() {
		return this._isAllRowsCheked;
	}

	_changeSelectionIndex: number;
	@Input() set changeSelectionIndex(value: number) {
		if (value != null) {
			this._changeSelectionIndex = value;
			this.selectedRow = value;
		}
	}
	get changeSelectionIndex() {
		return this._changeSelectionIndex;
	}

	@Output() loadTable: EventEmitter<IAction> = new EventEmitter();

	@Output() selectItem: EventEmitter<IAction> = new EventEmitter();
	@Output() selectAllItems: EventEmitter<IAction> = new EventEmitter();

	@Output() selectRowEvent: EventEmitter<IAction> = new EventEmitter();
	@Output() selectRowIndex: EventEmitter<number> = new EventEmitter();

	@Output() dbClickRowEvent: EventEmitter<IAction> = new EventEmitter();

	datagridLoader: StoreLoader = new StoreLoader(of(false), of(null));

	datagridLoaderSubject = new Subject<StoreLoader>();
	datagridLoader$ = this.datagridLoaderSubject.asObservable();

	@Input() currentColumn: IDatagridSettings<any>;
	@Input() withVerticalScroll: boolean = false; // Add class tableFixHead to fix header when scrolling in table body
	
	protected _highlightedOpIds: string[] = []
	@Input() set highlightedOpIds(value: string[]) {
		this._highlightedOpIds = value ? [...value] : [];
	};
	
	get highlightedOpIds() {
		return this._highlightedOpIds;
	}

	constructor(protected dataTableConfigService: DataTableConfigService) {
		super();
		this.datagridLoader$.subscribe(res => (this.datagridLoader = res));
	}

	selectRow(i: number, item): void {
		const isHighlighted = this._highlightedOpIds?.includes(item.operationId);
		if (isHighlighted) {
			this._highlightedOpIds = this._highlightedOpIds.filter(id => id !== item.operationId);
			this.selectedRow = null;
		} else if (this.selectedRow == null || this.selectedRow != i) {
			this.selectedRow = i;
		} else {
			this.selectedRow = null;
		}

		this.selectRowEvent.emit(item);
		this.selectRowIndex.emit(i);
	}

	selectAllRows($event): void {
		this.isAllRowsCheked = $event.target.checked;
		this.selectAllItems.emit($event.target.checked);
		this.nbCheckedElements = this.isAllRowsCheked ? this.list.totalCount : 0;
	}

	onDoubleClickRow(i: number, item): void {
		if (this.selectedRow == null || this.selectedRow != i) {
			this.selectedRow = i;
		} else {
			this.selectedRow = null;
		}
		this.dbClickRowEvent.emit(item);
	}

	onCheckItem(item: IAction) {
		this.selectItem.emit(item);
	}

	// Miroir du toggle de selectRow — à brancher sur (selectRowIndex) de l'enfant data-table-v2 
	// (selectRowIndex)="onChildSelectRow($event)"
	// pour maintenir selectedRow en sync depuis un composant héritier.
	onChildSelectRow(i: number): void {
		this.selectedRow = this.selectedRow === i ? null : i;
	}
	// À appeler en premier sur les éléments cliquables à l'intérieur des colonnes spéciales
	// (pictos, boutons). Bloque la propagation vers le <td> uniquement si la ligne est déjà
	// sélectionnée, évitant ainsi une désélection involontaire. Sinon laisse buller pour que
	// l'enfant sélectionne la ligne normalement via selectRow.
	managePropagation(e: MouseEvent, i: number): void {
		if (this.selectedRow === i) {
			e.stopPropagation();
		}
	}

	getCurrentConfig() {
		return this.dataTableConfigService.getDataGridSettingsByName(this.currentColumn.name);
	}

	dispatchNewSettings(settings: IDatagridSettings<any>) {
		let cols = this.dataTableConfigService.dataGridSettings$.value;
		if (cols.find(x => x.name == this.currentColumn.name)) {
			cols = cols.map(obj =>
				obj.name === this.currentColumn.name
					? {
							...settings
						}
					: obj
			);
		}

		this.dataTableConfigService.dataGridSettings$.next(cols);
		this.loadTable.emit();
	}

	trackColumnById(index: any, item: { id: any }) {
		return item ? item.id : undefined;
	}

	onSetSort(column: SortableCols): void {
		const currentConfig = this.getCurrentConfig();
		const currentSort = currentConfig.sort;
		const isSameCol = column === currentSort.column;

		const sort: IDatagridSort<SortableCols> = {
			column,
			isAsc: isSameCol ? !currentSort.isAsc : true
		};
		const settings: IDatagridSettings<SortableCols> = {
			options: currentConfig.options,
			pageNumber: currentConfig.pageNumber,
			pageSize: currentConfig.pageSize,
			sort: sort,
			name: currentConfig.name
		};
		this.dispatchNewSettings(settings);
	}

	getSortClasses(column: SortableCols): object {
		const currentSort = this.dataTableConfigService.getDataGridSettingsByName(this.currentColumn.name).sort;
		const isSameCol = column === currentSort.column;
		return {
			'col-sorted-asc': isSameCol && currentSort.isAsc,
			'col-sorted-desc': isSameCol && !currentSort.isAsc
		};
	}
}
